import { useCallback, useEffect, useMemo, useState } from "react";
import { Seo } from "@/site/seo";
import { Heading } from "@/site/components/Section";
import { api } from "@/site/api";
import { fmtDate } from "@/site/api";
import { toast } from "sonner";

/**
 * Private leads inbox. No product login exists yet, so access is a shared key
 * (MARKETING_LEADS_KEY on the backend) typed once per tab and kept in
 * sessionStorage — never in a cookie, never in the URL. The page is noindex,
 * disallowed in robots.txt and linked from nowhere. If the backend has no key
 * configured it answers 503 and the page says so plainly.
 */
const KEY_STORE = "postern.leads.key";
const STATUSES = ["new", "contacted", "qualified", "closed", "spam"];

const hdr = (key) => ({ headers: { "X-Leads-Key": key } });

function toCsv(rows) {
  const cols = ["created_at", "status", "intent", "name", "email", "company", "ams", "crm", "preferred_time", "message", "page", "utm_source", "utm_medium", "utm_campaign", "referrer", "note", "id"];
  const cell = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const line = (r) => cols.map((c) => cell(c.startsWith("utm_") || c === "referrer" ? r.utm?.[c] : r[c])).join(",");
  return [cols.join(","), ...rows.map(line)].join("\n");
}

function DeliveryDots({ d }) {
  const entries = Object.entries(d || {});
  if (!entries.length) return <span className="mono" style={{ color: "var(--faint)", fontSize: 11 }}>db only</span>;
  return (
    <span style={{ display: "inline-flex", gap: 6 }}>
      {entries.map(([ch, s]) => <span key={ch} className={`status status--${s.status === "delivered" ? "today" : s.status === "failed" ? "failed" : "pilot"}`} title={`${ch}: ${s.status}${s.http_status ? ` (${s.http_status})` : ""}`}>{ch}</span>)}
    </span>
  );
}

export default function AdminLeadsPage() {
  const [key, setKey] = useState(() => sessionStorage.getItem(KEY_STORE) || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async (k) => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/marketing/admin/leads", hdr(k));
      setData(r.data);
      sessionStorage.setItem(KEY_STORE, k);
      setKey(k);
    } catch (e) {
      const code = e?.response?.status;
      const msg = e?.response?.data?.detail || "Could not load leads.";
      setErr(typeof msg === "string" ? msg : "Could not load leads.");
      if (code === 401) { sessionStorage.removeItem(KEY_STORE); setKey(""); }
      setData(null);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { if (key) load(key); }, [key, load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/marketing/admin/leads/${id}`, { status }, hdr(key));
      setData((d) => ({ ...d, items: d.items.map((it) => (it.id === id ? { ...it, status } : it)) }));
      toast.success(`Marked ${status}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const items = useMemo(() => {
    const list = data?.items || [];
    const needle = q.trim().toLowerCase();
    return list.filter((it) => (filter === "all" || it.status === filter) && (!needle || [it.name, it.email, it.company, it.message].join(" ").toLowerCase().includes(needle)));
  }, [data, filter, q]);

  const download = () => {
    const blob = new Blob([toCsv(items)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `postern-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };

  const signOut = () => { sessionStorage.removeItem(KEY_STORE); setKey(""); setData(null); setInput(""); };

  const counts = useMemo(() => (data?.items || []).reduce((m, it) => ({ ...m, [it.status || "new"]: (m[it.status || "new"] || 0) + 1 }), {}), [data]);
  const anyChannel = data && Object.values(data.channels || {}).some(Boolean);

  return (
    <>
      <Seo title="Leads inbox" noindex />
      <section className="section">
        <div className="wrap" style={{ maxWidth: 1100 }}>
          <Heading eyebrow="Private · not linked from the site" title="Pilot requests" lede="Every submission of the pilot form, newest first. This page is the inbox until a mailbox and CRM exist; the same records are what HubSpot/email delivery will read from when those are configured." />

          {!key && (
            <form className="lead" style={{ maxWidth: 460 }} onSubmit={(e) => { e.preventDefault(); if (input.trim()) load(input.trim()); }} data-testid="leads-key-form">
              <div className="field"><label htmlFor="leads-key">Access key</label>
                <input id="leads-key" className="input" type="password" autoComplete="off" value={input} onChange={(e) => setInput(e.target.value)} placeholder="MARKETING_LEADS_KEY from the server .env" data-testid="leads-key-input" /></div>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12 }}>
                <button className="btn btn--primary" type="submit" disabled={busy || !input.trim()} data-testid="leads-key-submit">{busy ? "Checking…" : "Open inbox"}</button>
                <span className="hint">Kept in this tab only. Closing the tab forgets it.</span>
              </div>
              {err && <div className="notice" role="alert" style={{ marginTop: 12 }} data-testid="leads-error">{err}</div>}
            </form>
          )}

          {key && data && (
            <>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }} data-testid="leads-toolbar">
                <div className="seg" role="radiogroup" aria-label="Filter by status">
                  {["all", ...STATUSES].map((s) => <button key={s} type="button" role="radio" aria-checked={filter === s} className={`seg__btn ${filter === s ? "on" : ""}`} onClick={() => setFilter(s)} data-testid={`leads-filter-${s}`}>{s}<small>{s === "all" ? data.items.length : counts[s] || 0}</small></button>)}
                </div>
                <input className="input" style={{ maxWidth: 260 }} placeholder="Search name, email, company…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="leads-search" />
                <span style={{ flex: 1 }} />
                <button className="btn btn--ghost btn--sm" type="button" onClick={download} disabled={!items.length} data-testid="leads-export">Export CSV ({items.length})</button>
                <button className="btn btn--ghost btn--sm" type="button" onClick={() => load(key)} disabled={busy} data-testid="leads-refresh">{busy ? "Refreshing…" : "Refresh"}</button>
                <button className="btn btn--ghost btn--sm" type="button" onClick={signOut} data-testid="leads-signout">Forget key</button>
              </div>

              <div className="card" style={{ padding: "10px 14px", marginBottom: 14, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", fontSize: 13 }} data-testid="leads-channels">
                <span className="eyebrow" style={{ color: "var(--faint)" }}>Onward delivery</span>
                {Object.entries(data.channels || {}).map(([ch, on]) => <span key={ch} className={`status status--${on ? "today" : "off"}`}>{ch}: {on ? "on" : "off"}</span>)}
                {!anyChannel && <span style={{ color: "var(--muted)" }}>Nothing configured — leads stay here in the database. Set <code>LEAD_NOTIFY_EMAIL</code> + SMTP, HubSpot or a webhook on the server to switch a channel on; nothing on the site changes.</span>}
              </div>

              {items.length === 0 ? (
                <div className="card" style={{ color: "var(--muted)" }} data-testid="leads-empty">{data.items.length === 0 ? "No pilot requests yet. The first one lands here the moment the form is submitted." : "Nothing matches this filter."}</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="ctable leads-table" data-testid="leads-table">
                    <thead><tr><th>Received</th><th>Who</th><th>Company</th><th>Ask</th><th>Source</th><th>Delivery</th><th>Status</th></tr></thead>
                    <tbody>
                      {items.map((it) => (
                        <tr key={it.id} data-testid={`lead-row-${it.id}`}>
                          <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{fmtDate(it.created_at?.slice(0, 10))}<br /><span style={{ color: "var(--faint)" }}>{it.created_at?.slice(11, 16)} UTC</span></td>
                          <td><b>{it.name}</b><br /><a href={`mailto:${it.email}`} style={{ color: "var(--brass-dark)" }}>{it.email}</a></td>
                          <td>{it.company}<br /><span style={{ color: "var(--faint)", fontSize: 12 }}>{it.ams ? `${it.ams} AMs` : ""}{it.crm ? ` · ${it.crm}` : ""}</span></td>
                          <td style={{ maxWidth: 320 }}><span className={`status status--${it.intent === "walkthrough" ? "pilot" : "today"}`}>{it.intent}</span>{it.preferred_time && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>{it.preferred_time}</div>}{it.message && <div style={{ fontSize: 13, marginTop: 4, whiteSpace: "pre-wrap" }}>{it.message}</div>}</td>
                          <td style={{ fontSize: 12, color: "var(--muted)" }}>{it.page || "/"}{it.utm?.utm_source && <><br />{it.utm.utm_source}{it.utm.utm_medium ? ` / ${it.utm.utm_medium}` : ""}{it.utm.utm_campaign ? ` / ${it.utm.utm_campaign}` : ""}</>}{it.utm?.referrer && <><br /><span style={{ color: "var(--faint)" }}>{it.utm.referrer.replace(/^https?:\/\//, "").slice(0, 40)}</span></>}</td>
                          <td><DeliveryDots d={it.deliveries} /></td>
                          <td>
                            <select className="select" style={{ minWidth: 120, padding: "6px 8px", fontSize: 13 }} value={it.status || "new"} onChange={(e) => setStatus(it.id, e.target.value)} aria-label="Lead status" data-testid={`lead-status-${it.id}`}>
                              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {data.truncated && <p className="hint" style={{ marginTop: 10 }}>Showing the newest {data.returned}. Export for the full list.</p>}
            </>
          )}
          {key && !data && err && <div className="notice" role="alert" data-testid="leads-error">{err} <button className="btn btn--ghost btn--sm" type="button" onClick={signOut} style={{ marginLeft: 10 }}>Try another key</button></div>}
        </div>
      </section>
    </>
  );
}
